import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Code, Wrench, Zap, Cpu } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const Customization = () => {
  const features = [
    {
      icon: <Code className="w-10 h-10 text-[#00890b]" />,
      title: 'APIs & Integration',
      description: 'Windchill APIs and REST API development',
    },
    {
      icon: <Wrench className="w-10 h-10 text-[#00890b]" />,
      title: 'UI Customization',
      description: 'Form processors, table builders, and custom actions',
    },
    {
      icon: <Zap className="w-10 h-10 text-[#00890b]" />,
      title: 'Data Utilities',
      description: 'Bulk operations and data transformation tools',
    },
    {
      icon: <Cpu className="w-10 h-10 text-[#00890b]" />,
      title: 'Event Handlers',
      description: 'Listeners, validators, and automated actions',
    },
  ];

  const customizationTypes = [
    {
      category: 'APIs & Integration',
      items: [
        'Windchill Java APIs',
        'REST API Development',
        'Web Services Integration',
        'Third-Party System Integration',
      ],
    },
    {
      category: 'UI Customization',
      items: [
        'Form Processors',
        'Table Builders',
        'Custom Actions & Commands',
        'Wizard Development',
        'Custom JSP Pages',
        'Context Menus',
      ],
    },
    {
      category: 'Business Logic',
      items: [
        'Data Utilities',
        'Validators',
        'Event Listeners',
        'Custom Filters',
        'Report Generators',
        'Batch Processing',
      ],
    },
    {
      category: 'Advanced Development',
      items: [
        'Custom Workflows',
        'Expression Builders',
        'Method Servers',
        'Information Templates',
        'Custom Searches',
        'Navigation Customization',
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill Customization Expert | Outsource Windchill Development"
        description="Hire expert Windchill developers for APIs, Form Processors, and Table Builders. Outsource your Windchill customization to experienced professionals with 15+ years expertise."
        keywords="hire windchill developer, outsource windchill customization, hire windchill expert, windchill api developer, hire customization consultant, outsource windchill development"
        canonical="/hire-windchill-customization-expert"
      />
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Customization Specialists
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Windchill Customization Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your Windchill development to expert consultants. Hire experienced
              Windchill developers for custom APIs, Form Processors, and Table Builders
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Professional Windchill Customization
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Windchill provides extensive customization capabilities to tailor the system to your
            specific business needs. Our experienced Windchill developers have 15+ years of expertise
            in building custom solutions using Windchill APIs, form processors, table builders, data
            utilities, wizards, and event handlers.
          </p>
          <p className="text-lg text-gray-600">
            We follow PTC's development best practices and coding standards to deliver customizations
            that are maintainable, upgrade-safe, and performant.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Customization Areas</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Customization Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {customizationTypes.map((type, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-[#00890b]">{type.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {type.items.map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-[#00890b] flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Expert Windchill Development Services
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Looking to hire experienced Windchill developers? Our team specializes in complex
            Windchill customizations including:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Custom Actions & Filters</h3>
              <p className="text-gray-600">
                Extend Windchill's user interface with custom actions, context menu entries, and
                filtering logic tailored to your workflows.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Form Processors</h3>
              <p className="text-gray-600">
                Custom form processing logic for validation, pre-processing, and post-processing of
                user inputs and business objects.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Table Builders</h3>
              <p className="text-gray-600">
                Build custom table views with specialized columns, data formatting, and interactive
                capabilities.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Utilities & Wizards</h3>
              <p className="text-gray-600">
                Streamline complex operations with custom wizards and data utilities for bulk
                updates, imports, and transformations.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Outsource Windchill Customization
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Outsourcing your Windchill customization to our expert team provides several advantages:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold text-lg mb-2 text-[#00890b]">Deep Expertise</h4>
                <p className="text-gray-600">
                  15+ years of Windchill development experience across all modules and versions
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold text-lg mb-2 text-[#00890b]">Cost Effective</h4>
                <p className="text-gray-600">
                  Reduce costs compared to hiring and maintaining full-time Windchill developers
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold text-lg mb-2 text-[#00890b]">Rapid Delivery</h4>
                <p className="text-gray-600">
                  Fast-track your customization projects with our experienced development team
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};